import React, { useState, useEffect } from "react";
import EventsService from "../services/EventsService";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { format, subDays, startOfWeek, endOfWeek } from "date-fns";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

const CATEGORIES = ["work", "health", "leisure", "family", "self-care", "chores", "other"];
const CATEGORY_COLORS = {
  work: "#0A84FF",
  health: "#1E8E3A",
  leisure: "#E67E22",
  family: "#E91E63",
  "self-care": "#8E44AD",
  chores: "#B8960A",
  other: "#00BCD4",
};

function InsightsPage() {
  const [activeTab, setActiveTab] = useState("budget");

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a
              id="sidebar-toggle"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>Insights</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div className="timeline-tabs" style={{ marginBottom: 24 }}>
          {[
            { key: "budget", label: "Time Budget" },
            { key: "digest", label: "Daily Digest" },
            { key: "achievements", label: "Achievements" },
          ].map((t) => (
            <button
              key={t.key}
              className={`timeline-tab ${activeTab === t.key ? "timeline-tab-active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "budget" && <TimeBudgetSection />}
        {activeTab === "digest" && <DailyDigestSection />}
        {activeTab === "achievements" && <AchievementsSection />}
      </div>
    </>
  );
}

function TimeBudgetSection() {
  const [budgetData, setBudgetData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalCategory, setGoalCategory] = useState("work");
  const [goalHours, setGoalHours] = useState(8);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const ws = startOfWeek(new Date(), { weekStartsOn: 1 });
        const we = endOfWeek(new Date(), { weekStartsOn: 1 });
        const [budget, goalsList] = await Promise.all([
          EventsService.getTimeBudget(
            format(ws, "yyyy-MM-dd"),
            format(we, "yyyy-MM-dd")
          ),
          EventsService.listTimeBudgetGoals(),
        ]);
        setBudgetData(budget);
        setGoals(goalsList || []);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSetGoal = async () => {
    try {
      await EventsService.setTimeBudgetGoal({
        category: goalCategory,
        targetHoursPerWeek: goalHours,
      });
      const goalsList = await EventsService.listTimeBudgetGoals();
      setGoals(goalsList || []);
    } catch (err) {
      console.error(err.message);
    }
  };

  const rawBudget = Array.isArray(budgetData) ? budgetData : budgetData?.categories || [];
  const pieData = rawBudget
    .filter((c) => (c.hours || c.value || 0) > 0)
    .map((c, i) => ({
      name: c.category || c.name || "other",
      value: c.hours || c.value || 0,
      color: CATEGORY_COLORS[c.category || c.name] || actionsColorMap[i % actionsColorMap.length],
    }));

  const comparisonData = goals.map((g) => {
    const actual = rawBudget.find(
      (c) => (c.category || c.name) === g.category
    );
    return {
      name: g.category,
      target: g.targetHoursPerWeek || g.weeklyHours || 0,
      actual: actual?.hours || actual?.value || 0,
    };
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center" style={{ padding: 60 }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
        {/* Pie Chart */}
        <div className="glass-card" style={{ flex: "1 1 350px", padding: 24 }}>
          <h6 className="event-section-title" style={{ marginBottom: 16 }}>
            This Week's Time Distribution
          </h6>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value.toFixed(1)}h`}
                  labelLine={{ stroke: "rgba(255,255,255,0.2)" }}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "rgba(15,15,25,0.95)",
                    border: "0.5px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    color: "#fff",
                  }}
                  formatter={(v) => `${v.toFixed(1)} hours`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>
              No time budget data available for this week.
            </p>
          )}
        </div>

        {/* Actual vs Target */}
        {comparisonData.length > 0 && (
          <div className="glass-card" style={{ flex: "1 1 350px", padding: 24 }}>
            <h6 className="event-section-title" style={{ marginBottom: 16 }}>
              Actual vs Target
            </h6>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="name"
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                  label={{ value: "Hours", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.4)" }}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(15,15,25,0.95)",
                    border: "0.5px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    color: "#fff",
                  }}
                />
                <Bar dataKey="target" fill="rgba(255,255,255,0.15)" name="Target" radius={[6, 6, 0, 0]} />
                <Bar dataKey="actual" fill="#0A84FF" name="Actual" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Set Goal */}
      <div className="glass-card" style={{ padding: 20 }}>
        <h6 className="event-section-title" style={{ marginBottom: 12 }}>
          Set Weekly Goal
        </h6>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <select
            className="form-control"
            value={goalCategory}
            onChange={(e) => setGoalCategory(e.target.value)}
            style={{ flex: "1 1 150px", maxWidth: 200 }}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              className="form-control"
              value={goalHours}
              onChange={(e) => setGoalHours(parseInt(e.target.value) || 0)}
              min={0}
              max={168}
              style={{ width: 80, textAlign: "center" }}
            />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>hours/week</span>
          </div>
          <button className="btn btn-info" onClick={handleSetGoal}>
            Set Goal
          </button>
        </div>
      </div>
    </div>
  );
}

function DailyDigestSection() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [digest, setDigest] = useState(null);
  const [rating, setRating] = useState(0);
  const [ratingHistory, setRatingHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchDigest = async (date) => {
    setLoading(true);
    try {
      const [d, r, rRange] = await Promise.all([
        EventsService.getDailyDigest(date),
        EventsService.getDayRating(date).catch(() => null),
        EventsService.getDayRatingRange(
          format(subDays(new Date(date), 30), "yyyy-MM-dd"),
          date
        ).catch(() => []),
      ]);
      setDigest(d);
      setRating(r?.rating || 0);
      setRatingHistory(rRange || []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDigest(selectedDate);
  }, [selectedDate]);

  const handleRate = async (stars) => {
    setRating(stars);
    try {
      await EventsService.setDayRating({ date: selectedDate, rating: stars });
    } catch (err) {
      console.error(err.message);
    }
  };

  const ratingChartData = ratingHistory.map?.((r) => ({
    date: r.date ? format(new Date(r.date), "MMM d") : "",
    rating: r.rating || 0,
  })) || [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <input
          type="date"
          className="form-control"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ maxWidth: 200 }}
        />
      </div>

      {loading ? (
        <div className="d-flex justify-content-center" style={{ padding: 40 }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Digest Stats */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
            {[
              { label: "Active Activities", value: digest?.activeEvents ?? "—" },
              { label: "Total Actions", value: digest?.totalTimepoints ?? digest?.totalActions ?? "—", color: "#0A84FF" },
              { label: "Tracked Time", value: digest?.totalTrackedHours != null ? `${digest.totalTrackedHours.toFixed(1)}h` : (digest?.totalHours != null ? `${digest.totalHours.toFixed(1)}h` : "—") },
              { label: "Most Active Hour", value: digest?.mostActiveHour != null ? `${String(digest.mostActiveHour).padStart(2, "0")}:00` : "—" },
              { label: "Longest Gap", value: digest?.longestGapSeconds != null ? (digest.longestGapSeconds > 60 ? `${Math.floor(digest.longestGapSeconds / 60)}m ${digest.longestGapSeconds % 60}s` : `${digest.longestGapSeconds}s`) : (digest?.longestGap || "—") },
              { label: "vs 30-Day Avg", value: digest?.comparisonTo30DayAverage ? `${digest.comparisonTo30DayAverage.diff > 0 ? "+" : ""}${digest.comparisonTo30DayAverage.diff}` : (digest?.vsAverage || "—"), color: (digest?.comparisonTo30DayAverage?.diff > 0 || digest?.vsAveragePositive) ? "#30D158" : "#FF453A" },
            ].map((stat, i) => (
              <div key={i} className="glass-card" style={{ flex: "1 1 140px", padding: 16 }}>
                <p className="glass-card-label">{stat.label}</p>
                <p className="glass-card-value" style={{ color: stat.color || "#fff" }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {/* Day Rating */}
          <div className="glass-card" style={{ padding: 20, marginBottom: 24 }}>
            <h6 className="event-section-title" style={{ marginBottom: 12 }}>
              Rate Your Day
            </h6>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`star-btn ${star <= rating ? "star-btn-active" : ""}`}
                  onClick={() => handleRate(star)}
                >
                  <i className={`${star <= rating ? "fas" : "far"} fa-star`}></i>
                </button>
              ))}
            </div>
          </div>

          {/* Rating History Chart */}
          {ratingChartData.length > 0 && (
            <div className="glass-card" style={{ padding: 24 }}>
              <h6 className="event-section-title" style={{ marginBottom: 16 }}>
                Day Scores (Last 30 Days)
              </h6>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={ratingChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="date"
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                    angle={-30}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    domain={[0, 5]}
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(15,15,25,0.95)",
                      border: "0.5px solid rgba(255,255,255,0.12)",
                      borderRadius: 10,
                      color: "#fff",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rating"
                    stroke="#E67E22"
                    strokeWidth={2}
                    dot={{ fill: "#E67E22", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AchievementsSection() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const data = await EventsService.listAchievements();
        setAchievements(data || []);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center" style={{ padding: 60 }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (achievements.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <p style={{ fontSize: 48, marginBottom: 8 }}>🏆</p>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>
          No achievements yet. Keep tracking to unlock milestones!
        </p>
      </div>
    );
  }

  return (
    <div className="achievements-grid">
      {achievements.map((ach, i) => {
        const unlocked = ach.unlocked || ach.unlockedAt;
        return (
          <div
            key={ach.id || i}
            className={`glass-card achievement-card ${unlocked ? "achievement-unlocked" : "achievement-locked"}`}
          >
            <span className="achievement-icon">
              {ach.icon || (unlocked ? "🏆" : "🔒")}
            </span>
            <h6 className="achievement-name">{ach.name || ach.title}</h6>
            <p className="achievement-desc">{ach.description}</p>
            {unlocked && ach.unlockedAt && (
              <span className="achievement-date">
                {format(new Date(ach.unlockedAt), "MMM d, yyyy")}
              </span>
            )}
            {ach.progress !== undefined && !unlocked && (
              <div className="achievement-progress">
                <div
                  className="achievement-progress-bar"
                  style={{ width: `${Math.min(ach.progress, 100)}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default InsightsPage;
